import { useEffect, useRef } from 'react';

/**
 * Refetches the user's language list whenever the UI language changes.
 *
 * The backend localizes language names with the `locale` of each request, and the app caches the
 * list on the user context (containers/app.js `fetchLanguageList`). The language switcher only
 * dispatches a `changeLang` event, so without this the cached names stay in the previous language
 * while screens that fetch their own copy already show the new one (TUL-6099).
 *
 * `fetchLanguageList` closes over the current user, so the latest one is kept in a ref and the
 * listener is registered once. The function rejects with "Already fetching" as a no-op signal and
 * handles its own session errors, so a rejection is nothing for this hook to act on.
 */
const useRefetchLanguagesOnLangChange = (fetchLanguageList) => {
  const fetchLanguageListRef = useRef(fetchLanguageList);
  fetchLanguageListRef.current = fetchLanguageList;

  useEffect(() => {
    const refetch = () => {
      fetchLanguageListRef.current(true).catch(() => {});
    };
    document.addEventListener('changeLang', refetch);
    return () => {
      document.removeEventListener('changeLang', refetch);
    };
  }, []);
};

export default useRefetchLanguagesOnLangChange;
